import { useEffect, useRef, useState } from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { indiaMapStats } from '../../assets/index'
import { useReducedMotion } from '../../hooks/useReducedMotion'
import { useStatsScrollReveal } from '../../hooks/useStatsScrollReveal'
import { scale } from '../../theme/scale'
import {
  BOARD_REVEAL_EASING,
  BOARD_STATS_MAP_OPACITY_MIN,
  BOARD_STATS_MAP_SCALE_MIN,
  getStatsViewportClipHeight,
} from '../../utils/scrollAnimation'
import { HOME_MOBILE_MQ, homeMobilePaddingX } from '../home/homeMobileLayout'

const DESIGN_WIDTH = 1920
const DESIGN_HEIGHT = 1201

const MAP = {
  left: 428.9658203125,
  top: 94,
  width: 1163.06884765625,
  height: 1107,
}

const LEFT_STAT = {
  left: 200,
  top: 152,
  width: 368,
}

const RIGHT_STAT = {
  left: 1040,
  top: 900,
  width: 680,
}

const REVEAL = {
  map: { duration: 1100, delay: 200 },
  left: { duration: 900, delay: 1100 },
  right: { duration: 900, delay: 1750 },
}

function revealSx(visible, variant) {
  const { duration, delay } = REVEAL[variant]
  const hiddenTransform =
    variant === 'map'
      ? 'translate3d(0, 0, 0) scale(0.9)'
      : variant === 'left'
        ? 'translate3d(-36px, 0, 0)'
        : 'translate3d(36px, 0, 0)'

  return {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translate3d(0, 0, 0) scale(1)' : hiddenTransform,
    transformOrigin: variant === 'map' ? 'center center' : undefined,
    transition: `opacity ${duration}ms ${BOARD_REVEAL_EASING} ${delay}ms, transform ${duration}ms ${BOARD_REVEAL_EASING} ${delay}ms`,
    willChange: visible ? 'auto' : 'opacity, transform',
    '@media (prefers-reduced-motion: reduce)': {
      opacity: 1,
      transform: 'none',
      transition: 'none',
      willChange: 'auto',
    },
  }
}

function useScrollReveal() {
  const sectionRef = useRef(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const node = sectionRef.current
    if (!node) return undefined

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.18, rootMargin: '0px 0px -4% 0px' }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return { sectionRef, visible }
}

function StatBlock({ value, label, width }) {
  return (
    <Box sx={{ width, textAlign: 'center', color: '#111' }}>
      <Typography
        sx={{
          fontSize: scale.fontSize.statDisplay,
          lineHeight: 1,
          fontWeight: 400,
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontSize: scale.fontSize.statLabel,
          lineHeight: 1.2,
          fontWeight: 400,
          mt: '12px',
          whiteSpace: { xs: 'normal', lg: 'nowrap' },
        }}
      >
        {label}
      </Typography>
    </Box>
  )
}

function StatsCanvas({ visible, layerRefs, scrollDriven }) {
  const mapSx = scrollDriven
    ? {
        opacity: BOARD_STATS_MAP_OPACITY_MIN,
        transform: `translate3d(0, 0, 0) scale(${BOARD_STATS_MAP_SCALE_MIN})`,
        transformOrigin: 'center center',
        willChange: 'opacity, transform',
      }
    : revealSx(visible, 'map')

  const leftSx = scrollDriven
    ? {
        opacity: 0,
        transform: 'translate3d(-28px, 0, 0) scale(0.94)',
        willChange: 'opacity, transform',
      }
    : revealSx(visible, 'left')

  const rightSx = scrollDriven
    ? {
        opacity: 0,
        transform: 'translate3d(28px, 0, 0) scale(0.94)',
        willChange: 'opacity, transform',
      }
    : revealSx(visible, 'right')

  return (
    <>
      <Box
        component="img"
        ref={(node) => {
          if (layerRefs) layerRefs.current.map = node
        }}
        src={indiaMapStats}
        alt=""
        aria-hidden
        sx={{
          position: 'absolute',
          left: MAP.left,
          top: MAP.top,
          width: MAP.width,
          height: MAP.height,
          objectFit: 'contain',
          pointerEvents: 'none',
          ...mapSx,
        }}
      />

      <Box
        ref={(node) => {
          if (layerRefs) layerRefs.current.left = node
        }}
        sx={{
          position: 'absolute',
          left: LEFT_STAT.left,
          top: LEFT_STAT.top,
          ...leftSx,
        }}
      >
        <StatBlock value="10,000+" label="Youth Empowered" width={LEFT_STAT.width} />
      </Box>

      <Box
        ref={(node) => {
          if (layerRefs) layerRefs.current.right = node
        }}
        sx={{
          position: 'absolute',
          left: RIGHT_STAT.left,
          top: RIGHT_STAT.top,
          ...rightSx,
        }}
      >
        <StatBlock
          value="25,000+"
          label="Dreams Transformed Across India"
          width={RIGHT_STAT.width}
        />
      </Box>
    </>
  )
}

function ScaledStatsCanvas({ visible, layerRefs, scrollDriven }) {
  const containerRef = useRef(null)
  const [layoutScale, setLayoutScale] = useState(1)
  const [clipHeight, setClipHeight] = useState(() =>
    scrollDriven ? getStatsViewportClipHeight() : null,
  )

  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current) return
      const width = containerRef.current.offsetWidth
      setLayoutScale(Math.min(width / DESIGN_WIDTH, 1))
    }

    updateScale()
    const observer = new ResizeObserver(updateScale)
    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!scrollDriven) return undefined

    const updateClipHeight = () => setClipHeight(getStatsViewportClipHeight())
    updateClipHeight()
    window.addEventListener('resize', updateClipHeight)
    return () => window.removeEventListener('resize', updateClipHeight)
  }, [scrollDriven])

  const scaledCanvas = (
    <Box
      sx={{
        width: DESIGN_WIDTH,
        height: DESIGN_HEIGHT,
        transform: `scale(${layoutScale})`,
        transformOrigin: 'top left',
        position: 'relative',
        bgcolor: '#fffefa',
      }}
    >
      <StatsCanvas visible={visible} layerRefs={layerRefs} scrollDriven={scrollDriven} />
    </Box>
  )

  if (scrollDriven) {
    const contentHeight = DESIGN_HEIGHT * layoutScale

    return (
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          maxWidth: DESIGN_WIDTH,
          mx: 'auto',
        }}
      >
        <Box
          ref={(node) => {
            if (layerRefs) layerRefs.current.stage = node
          }}
          sx={{
            height: clipHeight ?? contentHeight,
            overflow: 'hidden',
            bgcolor: '#fffefa',
          }}
        >
          <Box
            ref={(node) => {
              if (layerRefs) layerRefs.current.pan = node
            }}
            sx={{
              width: DESIGN_WIDTH * layoutScale,
              height: contentHeight,
              willChange: 'transform',
              backfaceVisibility: 'hidden',
              transform: 'translate3d(0, -60px, 0)',
            }}
          >
            {scaledCanvas}
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        maxWidth: DESIGN_WIDTH,
        mx: 'auto',
        height: DESIGN_HEIGHT * layoutScale,
        overflow: 'hidden',
        bgcolor: '#fffefa',
      }}
    >
      {scaledCanvas}
    </Box>
  )
}

function DesktopScrollStatsSection() {
  const sectionRef = useRef(null)
  const pinRef = useRef(null)
  const layerRefs = useRef({ map: null, left: null, right: null })
  const reducedMotion = useReducedMotion()

  useStatsScrollReveal(sectionRef, pinRef, layerRefs, !reducedMotion)

  if (reducedMotion) {
    return (
      <Box component="section" sx={{ width: '100%', bgcolor: '#fffefa', overflow: 'hidden' }}>
        <ScaledStatsCanvas visible layerRefs={null} scrollDriven={false} />
      </Box>
    )
  }

  return (
    <Box
      ref={sectionRef}
      component="section"
      sx={{
        width: '100%',
        bgcolor: '#fffefa',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Box
        ref={pinRef}
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          boxSizing: 'border-box',
        }}
      >
        <ScaledStatsCanvas visible={false} layerRefs={layerRefs} scrollDriven />
      </Box>
    </Box>
  )
}

function MobileStatsLayout({ visible }) {
  return (
    <Box
      sx={{
        bgcolor: '#fffefa',
        py: { xs: '80px', sm: 8 },
        px: homeMobilePaddingX,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '60px', sm: 6 },
          maxWidth: 362,
          mx: 'auto',
          [HOME_MOBILE_MQ]: { gap: '60px' },
        }}
      >
        <Box sx={{ textAlign: 'center', color: '#111', ...revealSx(visible, 'left') }}>
          <Typography
            sx={{
              fontSize: { xs: 44, sm: 48 },
              lineHeight: 1,
              fontWeight: 400,
              [HOME_MOBILE_MQ]: { fontSize: 44 },
            }}
          >
            10,000+
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 17, sm: 32 },
              lineHeight: 1.2,
              fontWeight: 400,
              mt: '6px',
              [HOME_MOBILE_MQ]: { fontSize: 17, mt: '6px' },
            }}
          >
            Youth Empowered
          </Typography>
        </Box>

        <Box
          component="img"
          src={indiaMapStats}
          alt=""
          aria-hidden
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
            mx: 'auto',
            ...revealSx(visible, 'map'),
          }}
        />

        <Box sx={{ textAlign: 'center', color: '#111', ...revealSx(visible, 'right') }}>
          <Typography
            sx={{
              fontSize: { xs: 44, sm: 48 },
              lineHeight: 1,
              fontWeight: 400,
              [HOME_MOBILE_MQ]: { fontSize: 44 },
            }}
          >
            25,000+
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 17, sm: 32 },
              lineHeight: 1.2,
              fontWeight: 400,
              mt: '6px',
              [HOME_MOBILE_MQ]: { fontSize: 17, mt: '6px' },
            }}
          >
            Dreams Transformed Across India
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default function StatsSection() {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const { sectionRef, visible } = useScrollReveal()

  if (isDesktop) {
    return <DesktopScrollStatsSection />
  }

  return (
    <Box
      ref={sectionRef}
      component="section"
      sx={{ width: '100%', bgcolor: '#fffefa', overflow: 'hidden' }}
    >
      <MobileStatsLayout visible={visible} />
    </Box>
  )
}
